import { useEffect, useRef, useState } from 'react';
import api from '../../../../api';
import useFlashMessage from '../../../../hooks/useFlashMessage';

export function useRedacaoModule({ documento, permissoes: p, bloqueado, arquivos }) {
    const [lista, setLista] = useState([]),
        [item, setItem] = useState(null);
    const [sujo, setSujo] = useState(false),
        [arquivo, setArquivo] = useState(null);
    const [ocupado, setOcupado] = useState(false),
        [erro, setErro] = useState('');
    const [opcoes, setOpcoes] = useState([]),
        [pesquisa, setPesquisa] = useState('');
    const [buscandoPessoas, setBuscandoPessoas] = useState(false),
        [erroBusca, setErroBusca] = useState('');
    const busca = useRef(0);
    const ctx = useRef(0),
        trava = useRef(false),
        urls = useRef([]);
    const { setFlashMessage } = useFlashMessage();
    const base = `/arquivoDocumento/${documento.uuid}/redacoes`;
    const impedido = bloqueado || arquivos.pendente || arquivos.trabalhando || arquivos.carregando;
    const pendente = sujo || Boolean(arquivo);
    const podeEditar = p.autorizado && p.editarItem && documento.ativo && !impedido && !ocupado;
    const podeImprimir = p.autorizado && p.imprimirItem && !impedido && !ocupado;
    const podeCancelar = p.autorizado && p.cancelarItem && documento.ativo && !impedido && !ocupado;
    async function carregar(atual) {
        const { data } = await api.get(base);
        if (atual !== ctx.current) return;
        if (!Array.isArray(data)) throw new Error('Lista de redações inválida.');
        setLista(data);
    }
    const executar = async (fn) => {
        if (!documento.uuid || !p.autorizado || impedido || trava.current) return;
        const atual = ctx.current;
        trava.current = true;
        setOcupado(true);
        setErro('');
        try {
            await fn(atual);
        } catch (e) {
            let message = e.response?.data?.message || e.message;
            if (e.response?.data instanceof Blob)
                try {
                    message = JSON.parse(await e.response.data.text()).message || message;
                } catch {
                    /* mantém erro */
                }
            if (atual === ctx.current) {
                setErro(message);
                setFlashMessage(message, 'error');
            }
        } finally {
            if (atual === ctx.current) {
                trava.current = false;
                setOcupado(false);
            }
        }
    };
    useEffect(() => {
        const atual = ++ctx.current;
        busca.current++;
        setBuscandoPessoas(false);
        setErroBusca('');
        trava.current = false;
        setOcupado(false);
        setLista([]);
        setItem(null);
        setSujo(false);
        setArquivo(null);
        setOpcoes([]);
        setErro('');
        setPesquisa('');
        if (documento.uuid && p.autorizado) {
            trava.current = true;
            setOcupado(true);
            carregar(atual)
                .catch((e) => {
                    if (atual === ctx.current) setErro(e.response?.data?.message || e.message);
                })
                .finally(() => {
                    if (atual === ctx.current) {
                        trava.current = false;
                        setOcupado(false);
                    }
                });
        }
        return () => {
            ctx.current++;
            urls.current.forEach(URL.revokeObjectURL);
            urls.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documento.uuid, documento.empresaId, p.autorizado]);
    useEffect(() => {
        if (!pendente) return undefined;
        const aviso = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', aviso);
        return () => window.removeEventListener('beforeunload', aviso);
    }, [pendente]);
    const confirmarDescarte = () => !pendente || window.confirm('Descartar as alterações não salvas do editor?');
    const abrir = (row) => {
        if (!p.imprimirItem || !confirmarDescarte()) return;
        return executar(async (atual) => {
            const { data } = await api.get(`${base}/${row.uuid}`);
            if (atual !== ctx.current) return;
            setItem(data);
            setSujo(false);
            setArquivo(null);
            setOpcoes([]);
        });
    };
    const novo = () => {
        if (!podeEditar || !confirmarDescarte()) return;
        return executar(async (atual) => {
            const { data } = await api.get(`${base}/novo`);
            if (atual !== ctx.current) return;
            setItem(data);
            setSujo(true);
            setArquivo(null);
            setOpcoes([]);
        });
    };
    const limpar = () => {
        if (ocupado || impedido || !confirmarDescarte()) return;
        setItem(null);
        setSujo(false);
        setArquivo(null);
        setOpcoes([]);
        setErro('');
    };
    const copiar = () => {
        if (!podeEditar || !item || !confirmarDescarte()) return;
        setItem(
            ({
                titulo,
                texto,
                localEmissao,
                dataDocumento,
                apenasSocio,
                cargosEletivos,
                diretoria,
                conselho,
                assinaturas,
                camposVariaveis,
            }) => ({
                titulo: `${titulo.slice(0, 245)} — cópia`,
                texto,
                localEmissao,
                dataDocumento,
                apenasSocio,
                cargosEletivos,
                diretoria,
                conselho,
                assinaturas,
                camposVariaveis: { ...(camposVariaveis || {}) },
                variaveisResolvidas: null,
                textoResolvido: null,
                fechado: false,
                arquivos: [],
            })
        );
        setSujo(true);
        setArquivo(null);
        setOpcoes([]);
    };
    const alterar = (campo, value) => {
        if (!podeEditar || item?.fechado) return;
        setItem((i) => ({
            ...i,
            [campo]: value,
            ...(campo === 'cargosEletivos' && !value ? { diretoria: false, conselho: false } : {}),
        }));
        if (['cargosEletivos', 'diretoria', 'conselho'].includes(campo)) setOpcoes([]);
        setSujo(true);
    };
    const alterarCampoVariavel = (chave, value) => {
        if (!podeEditar || item?.fechado) return;
        setItem((i) => ({ ...i, camposVariaveis: { ...(i.camposVariaveis || {}), [chave]: value } }));
        setSujo(true);
    };
    const invalidarBusca = () => {
        busca.current++;
        setOpcoes([]);
        setBuscandoPessoas(false);
        setErroBusca('');
    };
    const alterarPesquisa = (value) => {
        if (!podeEditar || !item || item.fechado) return;
        invalidarBusca();
        setPesquisa(value);
    };
    const buscarPessoas = async (termo = pesquisa) => {
        if (!podeEditar || !item || item.fechado || trava.current) return;
        if (item.cargosEletivos && !item.diretoria && !item.conselho) {
            invalidarBusca();
            return;
        }
        const atual = ctx.current,
            numero = ++busca.current;
        setBuscandoPessoas(true);
        setErroBusca('');
        setOpcoes([]);
        try {
            const { data } = await api.get(`${base}/signatarios`, {
                params: {
                    cargosEletivos: item.cargosEletivos,
                    diretoria: item.diretoria,
                    conselho: item.conselho,
                    pesquisa: typeof termo === 'string' ? termo : pesquisa,
                },
            });
            if (atual !== ctx.current || numero !== busca.current) return;
            if (!Array.isArray(data)) throw new Error('Lista de signatários inválida.');
            setOpcoes(data);
        } catch (e) {
            if (atual === ctx.current && numero === busca.current) setErroBusca(e.response?.data?.message || e.message);
        } finally {
            if (atual === ctx.current && numero === busca.current) setBuscandoPessoas(false);
        }
    };
    useEffect(() => {
        invalidarBusca();
        if (!item || item.fechado || !podeEditar) return undefined;
        const timer = setTimeout(() => buscarPessoas(pesquisa), 350);
        return () => {
            clearTimeout(timer);
            busca.current++;
        };
        // A busca só depende do texto, escopo, filtros e disponibilidade, não das respostas recebidas.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        pesquisa,
        documento.uuid,
        documento.empresaId,
        Boolean(item),
        item?.uuid,
        item?.fechado,
        item?.cargosEletivos,
        item?.diretoria,
        item?.conselho,
        podeEditar,
    ]);
    const adicionar = (pessoa, cargo) => {
        if (!podeEditar || !item || item.fechado) return;
        if (item.assinaturas.some((s) => String(s.pessoaId) === String(pessoa.pessoaId))) {
            setFlashMessage('Pessoa já incluída. Remova a assinatura atual para trocar o cargo.', 'warning');
            return;
        }
        alterar('assinaturas', [
            ...item.assinaturas,
            {
                pessoaId: pessoa.pessoaId,
                nome: pessoa.nome,
                cpf: pessoa.cpf || '',
                cargoId: cargo?.cargoId || null,
                cargo: cargo?.cargo || '',
            },
        ]);
        setPesquisa('');
    };
    const salvar = () => {
        if (!podeEditar || item?.fechado || !sujo) return;
        return executar(async (atual) => {
            const { data } = await api.post(item.uuid ? `${base}/${item.uuid}/editar` : base, item);
            if (atual !== ctx.current) return;
            // O salvamento foi confirmado: uma falha no recarregamento não deve reenviar a inclusão.
            setItem(null);
            setSujo(false);
            const saved = await api.get(`${base}/${data.uuid}`);
            if (atual !== ctx.current) return;
            setItem(saved.data);
            setFlashMessage(data.message, 'success');
            await carregar(atual);
        });
    };
    const visualizar = () => {
        if (!podeImprimir || pendente || !item?.uuid || item.fechado) return;
        let janela = window.open('about:blank', '_blank');
        if (janela) {
            janela.opener = null;
            janela.document.body.textContent = 'Carregando prévia...';
        }
        return executar(async (atual) => {
            try {
                const { data } = await api.get(`${base}/${item.uuid}/previa`, {
                    responseType: 'blob',
                    params: { versao: item.versao },
                });
                if (atual !== ctx.current) return;
                const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                urls.current.push(url);
                if (janela && !janela.closed) {
                    janela.location.replace(url);
                    janela = null;
                } else {
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'previa-documento.pdf';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                }
            } finally {
                if (janela && !janela.closed) janela.close();
            }
        });
    };
    const atualizar = async (atual, response) => {
        if (atual !== ctx.current) return;
        setArquivo(null);
        setItem(null);
        setFlashMessage(response.data.message, 'success');
        const { data } = await api.get(`${base}/${item.uuid}`);
        if (atual !== ctx.current) return;
        setItem(data);
        setSujo(false);
        await carregar(atual);
        if (atual === ctx.current) await arquivos.recarregar();
    };
    const emitir = () => {
        if (!podeEditar || !podeImprimir || pendente || !item?.uuid || item.fechado) return;
        if (!window.confirm('Emitir e preservar o PDF? Alterações posteriores precisarão de uma cópia.')) return;
        return executar(async (atual) =>
            atualizar(atual, await api.post(`${base}/${item.uuid}/emitir`, { versao: item.versao }))
        );
    };
    const escolherArquivo = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!podeEditar || sujo || !item?.uuid || !file) return;
        if (!/\.pdf$/i.test(file.name) || file.size < 1 || file.size > 15 * 1024 * 1024) {
            setFlashMessage('Selecione um PDF de até 15 MB.', 'warning');
            return;
        }
        setArquivo(file);
    };
    const anexar = () => {
        if (!podeEditar || sujo || !arquivo || !item?.uuid) return;
        return executar(async (atual) => {
            const body = new FormData();
            body.append('arquivo', arquivo);
            body.append('versao', item.versao);
            await atualizar(atual, await api.post(`${base}/${item.uuid}/assinado`, body));
        });
    };
    const excluir = (row) => {
        if (!podeCancelar || pendente || !window.confirm(`Excluir a redação “${row.titulo}” e seus arquivos da listagem?`))
            return;
        return executar(async (atual) => {
            const { data } = await api.post(`${base}/${row.uuid}/excluir`, { versao: row.versao });
            if (atual !== ctx.current) return;
            if (item?.uuid === row.uuid) setItem(null);
            setFlashMessage(data.message, 'success');
            await carregar(atual);
            if (atual === ctx.current) await arquivos.recarregar();
        });
    };
    return {
        lista,
        item,
        sujo,
        pendente,
        arquivo,
        ocupado,
        impedido,
        erro,
        opcoes,
        pesquisa,
        setPesquisa: alterarPesquisa,
        buscandoPessoas,
        erroBusca,
        podeEditar,
        podeImprimir,
        podeCancelar,
        novo,
        limpar,
        copiar,
        alterar,
        alterarCampoVariavel,
        buscarPessoas,
        adicionar,
        salvar,
        abrir,
        visualizar,
        emitir,
        escolherArquivo,
        anexar,
        excluir,
        recarregar: () => executar(carregar),
    };
}
